import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  RefreshCw,
  ScanText,
  Server,
  Settings,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { api } from '../../lib/api';
import {
  Button,
  LifecycleControls,
  PageHeader,
  Select,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import { themedConfirm } from '../../lib/themed-dialogs';

const MAX_UPLOAD_BYTES = 30 * 1024 * 1024; // 30 MB — musí odpovídat limitu v ocr_submit_job

// ── Types ─────────────────────────────────────────────────────────────────────

type OcrResult = {
  name: string;
  status: 'pending' | 'running' | 'done' | 'error';
  text?: string;
  error?: string;
  elapsed_s?: number;
  mode?: string;
  confidence?: number;
  confidence_pct?: number;
  status_msg?: string;
};

type OcrJob = {
  status: 'running' | 'done' | 'error';
  results: OcrResult[];
  created: number;
};

type ModelStatus = { ok: boolean; url: string; disabled?: boolean };

type WorkerStatus = {
  ok: boolean;
  got?: ModelStatus;
  qwen?: ModelStatus;
  container?: { running: boolean; memory?: string; uptime?: string };
  config?: OcrConfig;
};

type OcrConfig = {
  worker_url: string;
  fallback_url: string;
  default_mode: 'fast' | 'quality';
  enable_fallback: boolean;
  quality_enabled: boolean;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(bin);
}

/** Job upload přes MCP — soubory jdou jako base64, žádný přímý REST z UI. */
async function uploadJob(
  files: File[],
  mode: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): Promise<{ job_id: string; count: number }> {
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  if (totalBytes > MAX_UPLOAD_BYTES) {
    throw new Error(
      t('ocr.toastFilesTooLarge', { mb: (totalBytes / 1024 / 1024).toFixed(1) }),
    );
  }
  const encoded = await Promise.all(
    files.map(async (f) => ({ name: f.name, content_base64: await fileToBase64(f) })),
  );
  return await api.mcp<{ job_id: string; count: number }>('ocr.submit_job', {
    files: encoded,
    mode,
  });
}

function exportTxt(results: OcrResult[]) {
  const txt = results.map((r) => `=== ${r.name} ===\n${r.text || r.error || ''}`).join('\n\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([txt], { type: 'text/plain' }));
  a.download = 'ocr_results.txt';
  a.click();
}

function confidenceColor(pct?: number) {
  if (pct == null) return 'text-gray-500';
  if (pct >= 75) return 'text-emerald-400';
  if (pct >= 50) return 'text-amber-400';
  return 'text-red-400';
}

// ── Components ────────────────────────────────────────────────────────────────

function ModelRow({ label, model, tag }: { label: string; model?: ModelStatus; tag: string }) {
  const { t } = useTranslation();
  if (model?.disabled) {
    return (
      <div className='flex items-center gap-2 py-1.5'>
        <div className='w-2 h-2 rounded-full bg-gray-700 flex-shrink-0' />
        <span className='text-xs text-gray-600'>{label}</span>
        <span className='text-[10px] text-gray-700 ml-auto'>{t('ocr.disabledState')}</span>
      </div>
    );
  }
  const ok = model?.ok;
  return (
    <div className='flex items-center gap-2 py-1.5'>
      {ok
        ? <CheckCircle size={12} className='text-emerald-400 flex-shrink-0' />
        : <XCircle size={12} className='text-gray-600 flex-shrink-0' />}
      <span className={`text-xs ${ok ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
      <span className='text-[10px] text-gray-600 font-mono ml-auto'>{tag}</span>
    </div>
  );
}

function WorkerCard({ status }: { status?: WorkerStatus }) {
  const { t } = useTranslation();
  const localRunning = status?.container?.running;

  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-4 space-y-3'>
      {/* Lokální kontejner */}
      <div className='flex items-center gap-2'>
        <Server size={13} className={localRunning ? 'text-emerald-400' : 'text-gray-600'} />
        <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
          {t('ocr.localContainer')}
        </span>
        <span className={`text-xs ml-auto ${localRunning ? 'text-emerald-400' : 'text-gray-600'}`}>
          {localRunning ? t('ocr.running') : t('ocr.offline')}
        </span>
      </div>
      <div className='h-px bg-gray-800' />
      {/* Modely */}
      <div className='space-y-0.5'>
        <ModelRow label='GOT-OCR2.0' model={status?.got} tag='fast' />
        <ModelRow label='Qwen2-VL' model={status?.qwen} tag='quality' />
      </div>
    </div>
  );
}

function ResultItem({ item }: { item: OcrResult }) {
  const [expanded, setExpanded] = useState(false);
  const icon = item.status === 'done'
    ? <CheckCircle size={13} className='text-emerald-400 shrink-0' />
    : item.status === 'error'
    ? <XCircle size={13} className='text-red-400 shrink-0' />
    : item.status === 'running'
    ? <Loader2 size={13} className='text-blue-400 animate-spin shrink-0' />
    : <div className='w-3 h-3 rounded-full border border-gray-600 shrink-0' />;

  return (
    <div className='rounded-xl border border-gray-800 bg-gray-950/50'>
      <div
        className='flex items-center gap-2 px-3 py-2.5 cursor-pointer'
        onClick={() => item.status === 'done' && setExpanded((e) => !e)}
      >
        {icon}
        <span className='text-sm text-white truncate flex-1'>{item.name}</span>
        {item.status === 'running' && item.status_msg && (
          <span className='text-xs text-gray-500 shrink-0'>{item.status_msg}</span>
        )}
        {item.status === 'done' && (
          <>
            {item.confidence_pct != null && (
              <span className={`text-xs shrink-0 ${confidenceColor(item.confidence_pct)}`}>
                {item.confidence_pct}%
              </span>
            )}
            <span className='text-xs text-gray-600 shrink-0'>{item.elapsed_s}s</span>
            <span className='text-xs text-gray-600 shrink-0'>{item.mode}</span>
            {expanded
              ? <ChevronUp size={12} className='text-gray-600' />
              : <ChevronDown size={12} className='text-gray-600' />}
          </>
        )}
        {item.status === 'error' && (
          <span className='text-xs text-red-400 shrink-0 truncate max-w-48'>{item.error}</span>
        )}
      </div>
      {expanded && item.text && (
        <div className='border-t border-gray-800 px-3 py-2.5'>
          <pre className='text-xs text-gray-300 whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed'>{item.text}</pre>
        </div>
      )}
    </div>
  );
}

function DropZone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    if (files.length) onFiles(files);
  }, [onFiles]);

  return (
    <div
      className={`rounded-2xl border-2 border-dashed transition-colors cursor-pointer p-8 text-center ${
        over ? 'border-blue-500 bg-blue-500/5' : 'border-gray-700 hover:border-gray-600'
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <Upload size={24} className='text-gray-600 mx-auto mb-3' />
      <p className='text-sm text-gray-400'>{t('ocr.dropHint')}</p>
      <p className='text-xs text-gray-600 mt-1'>JPG, PNG, WEBP, TIFF, PDF</p>
      <input
        ref={inputRef}
        type='file'
        multiple
        accept='image/*,.pdf,.tiff'
        className='hidden'
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

const GOT_LOCAL_URL = 'http://localhost:8002';
const GOT_REMOTE_URL = 'https://10.118.200.18:8004';
const QWEN_REMOTE_URL = 'https://10.118.200.18:8001';

// ── Settings panel ────────────────────────────────────────────────────────────

function SettingsPanel() {
  const { t } = useTranslation();
  const [form, setForm] = useState<OcrConfig | null>(null);

  const cfgQ = useQuery<{ config: OcrConfig }>({
    queryKey: ['ocr-config'],
    queryFn: () =>
      api.mcp<{ config: OcrConfig }>('ocr.config', { action: 'get' }),
    staleTime: Infinity, // nesmí auto-přepsat uživatelovy změny
  });

  // Naplní form pouze tehdy, pokud ještě není inicializovaný
  useEffect(() => {
    if (cfgQ.data?.config && form === null) {
      setForm(cfgQ.data.config);
    }
  }, [cfgQ.data, form]);

  const saveMut = useMutation({
    mutationFn: (cfg: OcrConfig) =>
      api.mcp<{ ok: boolean; config: OcrConfig }>('ocr.config', {
        action: 'set',
        ...cfg,
      }),
    onSuccess: (res) => {
      // Nastaví form na to, co server skutečně uložil
      setForm(res.config);
      toast.success(t('ocr.toastConfigSaved'));
    },
    onError: (e: Error) => toast.error(t('ocr.toastError', { message: e.message })),
  });

  if (!form) return <div className='text-xs text-gray-500'>{t('ocr.loading')}</div>;

  const inp =
    'w-full rounded-xl border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 font-mono text-xs';
  const gotIsLocal = form.worker_url === GOT_LOCAL_URL;

  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-5'>
      <h2 className='text-sm font-medium text-white flex items-center gap-2'>
        <Settings size={14} className='text-gray-400' /> {t('ocr.settingsTitle')}
      </h2>

      {/* GOT-OCR2.0 */}
      <div className='rounded-xl border border-gray-700/60 p-4 space-y-3'>
        <div className='flex items-center gap-2'>
          <span className='text-xs font-semibold text-violet-400 uppercase tracking-wider'>
            GOT-OCR2.0
          </span>
          <span className='text-xs text-gray-600'>{t('ocr.gotSubtitle')}</span>
        </div>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1.5'>{t('ocr.urlLabel')}</div>
          <input
            className={inp}
            value={form.worker_url}
            disabled={gotIsLocal}
            onChange={(e) => setForm((f) => ({ ...f, worker_url: e.target.value }))}
            placeholder={GOT_REMOTE_URL}
          />
        </label>
        <label className='flex items-center gap-2 cursor-pointer select-none'>
          <input
            type='checkbox'
            checked={gotIsLocal}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                worker_url: e.target.checked ? GOT_LOCAL_URL : GOT_REMOTE_URL,
              }))}
            className='w-4 h-4 accent-violet-500 rounded'
          />
          <span className='text-sm text-gray-300'>{t('ocr.useLocalContainer')}</span>
          <span className='text-xs text-gray-600 font-mono'>{GOT_LOCAL_URL}</span>
        </label>
      </div>

      {/* Qwen2-VL */}
      <div
        className={`rounded-xl border p-4 space-y-3 transition-colors ${
          form.quality_enabled ? 'border-gray-700/60' : 'border-gray-800/40 opacity-60'
        }`}
      >
        <div className='flex items-center gap-2'>
          <span className='text-xs font-semibold text-blue-400 uppercase tracking-wider'>
            Qwen2-VL
          </span>
          <span className='text-xs text-gray-600'>{t('ocr.qwenSubtitle')}</span>
          <label className='flex items-center gap-1.5 ml-auto cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={form.quality_enabled}
              onChange={(e) => setForm((f) => ({ ...f, quality_enabled: e.target.checked }))}
              className='w-3.5 h-3.5 accent-blue-500 rounded'
            />
            <span className='text-xs text-gray-400'>{t('ocr.enabled')}</span>
          </label>
        </div>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1.5'>{t('ocr.urlLabel')}</div>
          <input
            className={inp}
            value={form.fallback_url}
            disabled={!form.quality_enabled}
            onChange={(e) => setForm((f) => ({ ...f, fallback_url: e.target.value }))}
            placeholder={QWEN_REMOTE_URL}
          />
        </label>
      </div>

      {/* Výchozí režim + fallback */}
      <div className='grid grid-cols-2 gap-3'>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1.5'>{t('ocr.defaultMode')}</div>
          <Select
            className='w-full'
            value={form.default_mode}
            onChange={(e) =>
              setForm((f) => ({ ...f, default_mode: e.target.value as 'fast' | 'quality' }))}
          >
            <option value='fast'>{t('ocr.modeFastOption')}</option>
            <option value='quality'>{t('ocr.modeQualityOption')}</option>
          </Select>
        </label>
        <label className='flex items-center gap-2 pt-6 cursor-pointer select-none'>
          <input
            type='checkbox'
            checked={form.enable_fallback}
            onChange={(e) => setForm((f) => ({ ...f, enable_fallback: e.target.checked }))}
            className='w-4 h-4 accent-blue-500 rounded'
          />
          <span className='text-sm text-gray-300'>{t('ocr.enableFallback')}</span>
        </label>
      </div>

      <Button
        variant='primary'
        size='md'
        loading={saveMut.isPending}
        onClick={() => saveMut.mutate(form)}
      >
        {t('ocr.save')}
      </Button>
    </div>
  );
}

// ── GOT Deploy panel ─────────────────────────────────────────────────────────

type GotStatus = {
  exists: boolean;
  running: boolean;
  restarting: boolean;
  exit_code?: number;
  restart_count?: number;
  id?: string;
  deploying: boolean;
  deploy_error?: string | null;
};

function GotDeployPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // GOT local container lifecycle — ocr.got (ocr.manage = remote worker)
  const statusQ = useQuery<GotStatus>({
    queryKey: ['ocr-got-status'],
    queryFn: () => api.mcp<GotStatus>('ocr.got', { action: 'status' }),
    // Rychlý polling dokud probíhá deploy
    refetchInterval: (q) => (q.state.data?.deploying ? 3000 : 15_000),
  });

  const deployMut = useMutation({
    mutationFn: () => api.mcp('ocr.got', { action: 'deploy' }),
    onSuccess: () => {
      toast.success(t('ocr.toastDeployStarted'));
      qc.invalidateQueries({ queryKey: ['ocr-got-status'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const manageMut = useMutation({
    mutationFn: (action: string) =>
      api.mcp('ocr.got', { action: 'manage', manage_action: action }),
    onSuccess: (_r, action) => {
      toast.success(t('ocr.toastGotAction', { action }));
      qc.invalidateQueries({ queryKey: ['ocr-got-status'] });
      qc.invalidateQueries({ queryKey: ['ocr-status'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const s = statusQ.data;
  const deploying = s?.deploying ?? false;
  const exists = s?.exists ?? false;
  const running = s?.running ?? false;
  const restarting = s?.restarting ?? false;

  const stateLabel = deploying
    ? t('ocr.stateDeploying')
    : restarting
    ? t('ocr.stateRestartLoop')
    : running
    ? t('ocr.running')
    : exists
    ? t('ocr.stateStopped')
    : t('ocr.stateNotExist');
  const stateColor = deploying
    ? 'text-amber-400'
    : restarting
    ? 'text-red-400'
    : running
    ? 'text-emerald-400'
    : exists
    ? 'text-gray-400'
    : 'text-gray-600';


  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-4'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <h2 className='text-sm font-medium text-white flex items-center gap-2'>
          <ScanText size={14} className='text-violet-400' />
          {t('ocr.gotDeployTitle')}
        </h2>
        <span className={`text-xs font-medium flex items-center gap-1.5 ${stateColor}`}>
          {deploying && <Loader2 size={11} className='animate-spin' />}
          {restarting && <RefreshCw size={11} className='animate-spin' />}
          {stateLabel}
          {restarting && s?.restart_count != null && (
            <span className='text-red-600 font-mono'>×{s.restart_count}</span>
          )}
          {s?.id && !deploying && !restarting && (
            <span className='text-gray-600 font-mono'>{s.id}</span>
          )}
        </span>
      </div>

      {/* Restart loop varování */}
      {restarting && !deploying && (
        <div className='rounded-xl bg-red-500/5 border border-red-500/20 px-3 py-2.5 flex items-start gap-2'>
          <AlertTriangle size={13} className='text-red-400 mt-0.5 flex-shrink-0' />
          <div>
            <p className='text-xs text-red-300 font-medium'>{t('ocr.restartLoopTitle')}</p>
            <p className='text-xs text-red-400/70 mt-0.5'>
              {t('ocr.restartLoopDetail', { exitCode: s?.exit_code ?? '', count: s?.restart_count ?? '' })}
            </p>
          </div>
        </div>
      )}

      {/* Chyba deploye */}
      {s?.deploy_error && (
        <div className='rounded-xl bg-red-500/5 border border-red-500/20 px-3 py-2.5'>
          <p className='text-xs text-red-400 font-mono whitespace-pre-wrap'>{s.deploy_error}</p>
        </div>
      )}

      {/* Nasadit — pokud kontejner NEEXISTUJE nebo je v restart loopu */}
      {(!exists || restarting) && !deploying && (
        <>
          <p className='text-xs text-gray-500'>
            {t('ocr.deployIntroPre')}{' '}
            <code className='text-gray-300'>acm-ocr-models</code>. {t('ocr.deployIntroPost')}
          </p>
          <Button
            variant='primary'
            size='md'
            loading={deployMut.isPending}
            icon={<Upload size={13} />}
            onClick={() => deployMut.mutate()}
          >
            {t('ocr.deployBtn')}
          </Button>
        </>
      )}

      {/* Probíhá deploy */}
      {deploying && (
        <p className='text-xs text-amber-400/80 flex items-center gap-2'>
          <Loader2 size={12} className='animate-spin' />
          {t('ocr.deployInProgress')}
        </p>
      )}

      {/* Ovládání — pouze pokud kontejner EXISTUJE a nedeploys */}
      {exists && !deploying && (
        <div className='flex items-center gap-2 flex-wrap'>
          <LifecycleControls.Bar
            name='acm-ocr-got'
            running={running}
            loading={manageMut.isPending}
            onAction={(action) => manageMut.mutate(action)}
          />
          <Button
            variant='danger'
            size='sm'
            icon={<Trash2 size={11} />}
            disabled={manageMut.isPending}
            onClick={async () => {
              if (await themedConfirm(t('ocr.confirmRemoveContainer'))) manageMut.mutate('remove');
            }}
          >
            {t('ocr.remove')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function OcrModule() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'ocr' | 'settings'>('ocr');
  const [mode, setMode] = useState<'fast' | 'quality'>('fast');
  const [files, setFiles] = useState<File[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);

  const statusQ = useQuery<WorkerStatus>({
    queryKey: ['ocr-status'],
    queryFn: async () => {
      // ocr.status bez job_id → worker health + container (got/qwen detail z REST chybí)
      const r = await api.mcp<{
        ok?: boolean;
        worker?: unknown;
        container?: WorkerStatus['container'];
      }>('ocr.status', {}, { silent: true });
      const detail = (r.worker && typeof r.worker === 'object')
        ? r.worker as Record<string, unknown>
        : {};
      const gotOk = r.ok === true || detail.ok === true;
      return {
        ok: !!r.ok,
        got: { ok: gotOk, url: String(detail.url ?? '') },
        qwen: detail.qwen && typeof detail.qwen === 'object'
          ? detail.qwen as ModelStatus
          : undefined,
        container: r.container,
      };
    },
    refetchInterval: 30_000,
  });

  const jobQ = useQuery<OcrJob>({
    queryKey: ['ocr-job', jobId],
    queryFn: async () => {
      const r = await api.mcp<{
        ok?: boolean;
        error?: string;
        status?: OcrJob['status'];
        results?: Array<OcrResult & { model?: string }>;
      }>('ocr.status', { job_id: jobId! }, { silent: true });
      if (r.ok === false) throw new Error(r.error || 'Job nenalezen');
      return {
        status: r.status ?? 'error',
        results: (r.results ?? []).map((item) => ({
          ...item,
          mode: item.mode ?? item.model,
        })),
        created: 0,
      };
    },
    enabled: !!jobId,
    retry: false, // při 404 nezkoušej znovu
    refetchInterval: (q) => {
      if (q.state.error || q.state.data?.status !== 'running') return false;
      return 2000;
    },
  });

  // Multipart binary upload — REST exception (viz uploadJob)
  const submitMut = useMutation({
    mutationFn: () => uploadJob(files, mode, t),
    onSuccess: (res) => {
      setJobId(res.job_id);
      setFiles([]);
      toast.success(t('ocr.toastJobStarted', { count: res.count }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const manageMut = useMutation({
    mutationFn: (action: string) => api.mcp('ocr.manage', { action }),
    onSuccess: (_, action) => {
      toast.success(t('ocr.toastWorkerAction', { action }));
      qc.invalidateQueries({ queryKey: ['ocr-status'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const job = jobQ.data;
  const done = job?.results.filter((r) => r.status === 'done' || r.status === 'error').length ?? 0;
  const total = job?.results.length ?? 0;
  const pct = total ? Math.round(done / total * 100) : 0;
  const allDone = job?.status === 'done';

  return (
    <div className='space-y-5'>
      <PageHeader
        title={t('ocr.title')}
        description={t('ocr.subtitle')}
        icon={ScanText}
        actions={
          <Button
            variant='ghost'
            size='icon'
            onClick={() => qc.invalidateQueries({ queryKey: ['ocr-status'] })}
          >
            <RefreshCw size={13} />
          </Button>
        }
      />

      <Tabs defaultTab='ocr'>
        <TabList>
          <Tab id='ocr'>
            <ScanText size={12} />{t('ocr.tabOcr')}
          </Tab>
        </TabList>

        <TabPanel id='ocr'>
          {/* Tabs */}
          <div className='flex gap-1 border-b border-gray-800 pb-0'>
            {[{ id: 'ocr', label: t('ocr.tabOcr') }, { id: 'settings', label: t('ocr.tabSettings') }].map((tabItem) => (
              <button
                key={tabItem.id}
                onClick={() => setTab(tabItem.id as 'ocr' | 'settings')}
                className={`px-4 py-2 text-xs rounded-t-lg border-b-2 -mb-px transition-colors ${
                  tab === tabItem.id
                    ? 'text-blue-400 border-blue-500 bg-blue-500/5'
                    : 'text-gray-500 border-transparent hover:text-white'
                }`}
              >
                {tabItem.label}
              </button>
            ))}
          </div>

          {tab === 'settings' && (
            <div className='space-y-5'>
              <SettingsPanel />
              <GotDeployPanel />
            </div>
          )}

          {tab === 'ocr' && (
            <div className='grid grid-cols-1 xl:grid-cols-3 gap-5'>
              {/* Left — upload + worker */}
              <div className='xl:col-span-1 space-y-4'>
                <WorkerCard status={statusQ.data} />

                {/* Worker controls */}
                <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-4 space-y-3'>
                  <h2 className='text-xs text-gray-500 font-medium uppercase tracking-wider'>
                    {t('ocr.workerTitle')}
                  </h2>
                  <LifecycleControls.Bar
                    name='ocr-worker'
                    running={statusQ.data?.container?.running ?? false}
                    loading={manageMut.isPending}
                    onAction={(action) => {
                      manageMut.mutate(action);
                    }}
                  />
                </div>

                {/* Upload */}
                <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-4 space-y-3'>
                  <h2 className='text-xs text-gray-500 font-medium uppercase tracking-wider'>
                    {t('ocr.newJob')}
                  </h2>
                  <div className='flex gap-2'>
                    {(['fast', 'quality'] as const).map((m) => (
                      <button
                        key={m}
                        onClick={() => setMode(m)}
                        className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors ${
                          mode === m
                            ? 'border-blue-500/40 bg-blue-500/10 text-blue-400'
                            : 'border-gray-700 text-gray-500 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        {m === 'fast' ? t('ocr.modeFast') : t('ocr.modeQuality')}
                      </button>
                    ))}
                  </div>

                  {files.length > 0
                    ? (
                      <div className='space-y-1.5'>
                        <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto'>
                          {files.map((f) => (
                            <div
                              key={f.name}
                              className='flex items-center gap-1 bg-gray-800 rounded-lg px-2 py-1 text-xs text-gray-300'
                            >
                              <span className='max-w-32 truncate'>{f.name}</span>
                              <button
                                onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}
                              >
                                <XCircle size={11} className='text-gray-600 hover:text-red-400' />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className='flex gap-2'>
                          <button
                            onClick={() => submitMut.mutate()}
                            disabled={submitMut.isPending}
                            className='flex-1 py-2 text-sm rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 font-medium transition-colors flex items-center justify-center gap-2'
                          >
                            {submitMut.isPending
                              ? <Loader2 size={13} className='animate-spin' />
                              : <Upload size={13} />}
                            {submitMut.isPending ? t('ocr.sending') : t('ocr.startOcr', { count: files.length })}
                          </button>
                          <button
                            onClick={() => setFiles([])}
                            className='p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl'
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    )
                    : (
                      <DropZone
                        onFiles={(f) =>
                          setFiles((prev) => [
                            ...prev,
                            ...f.filter((n) => !prev.find((x) => x.name === n.name)),
                          ])}
                      />
                    )}
                </div>
              </div>

              {/* Right — job results */}
              <div className='xl:col-span-2 rounded-2xl border border-gray-800 bg-gray-900/30 p-4 space-y-3'>
                <div className='flex items-center justify-between'>
                  <h2 className='text-sm font-medium text-white flex items-center gap-2'>
                    <Activity size={14} className='text-violet-400' />
                    {t('ocr.results')}
                    {jobId && <span className='text-xs text-gray-600 font-mono'>{jobId}</span>}
                  </h2>
                  {allDone && job && (
                    <div className='flex gap-2'>
                      <button
                        onClick={() => exportTxt(job.results)}
                        className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/30'
                      >
                        <Download size={11} /> {t('ocr.exportTxt')}
                      </button>
                      <button
                        onClick={() => setJobId(null)}
                        className='px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                      >
                        {t('ocr.newJob')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Chyba jobu — job nenalezen (worker restartoval) */}
                {jobQ.isError && jobId && (
                  <div className='flex flex-col items-center justify-center h-32 gap-3'>
                    <XCircle size={20} className='text-red-400 opacity-60' />
                    <p className='text-sm text-red-400'>
                      {t('ocr.jobNotFound')}
                    </p>
                    <button
                      onClick={() => setJobId(null)}
                      className='px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                    >
                      {t('ocr.newJob')}
                    </button>
                  </div>
                )}

                {/* Progress bar */}
                {jobId && !allDone && !jobQ.isError && (
                  <div className='space-y-1.5'>
                    <div className='flex justify-between text-xs text-gray-500'>
                      <span>
                        {job?.results.find((r) => r.status === 'running')?.name || t('ocr.waiting')}
                      </span>
                      <span>{done}/{total}</span>
                    </div>
                    <div className='h-1.5 bg-gray-800 rounded-full overflow-hidden'>
                      <div
                        className='h-full bg-blue-500 transition-all duration-500 rounded-full'
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Results list */}
                {!jobId || jobQ.isError
                  ? (
                    <div className='flex flex-col items-center justify-center h-48 gap-3 text-gray-600'>
                      <ScanText size={32} className='opacity-20' />
                      <p className='text-sm'>{t('ocr.emptyHint')}</p>
                    </div>
                  )
                  : (
                    <div className='space-y-2 max-h-[500px] overflow-y-auto pr-1'>
                      {(job?.results ?? []).map((item, i) => <ResultItem key={i} item={item} />)}
                      {!job && (
                        <div className='flex items-center justify-center h-32 gap-2 text-gray-600'>
                          <Loader2 size={14} className='animate-spin' />
                          <span className='text-sm'>{t('ocr.loading')}</span>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          )}
        </TabPanel>
      </Tabs>
    </div>
  );
}
